import { DateTime } from 'luxon';
import { ReferralAuthorization, ReferralAuthorizationStatus } from '@sondermind/utilities/models-patient';

export interface VaAuthorizationRowVm {
  authNumber: string;
  careType: string;
  dateRange: string;
  statusLabel: string;
  badgeClass: string;
}

interface StatusStyle {
  badgeClass: string;
  label: string;
}

const DATE_FORMAT = 'MM/dd/yy';
const MISSING_DATE = '—';

// Wire status -> presentation. Note the single-L wire value `canceled` maps to
// the double-L display label `Cancelled` (per Figma).
export const VA_AUTH_STATUS_STYLE: Record<ReferralAuthorizationStatus, StatusStyle> = {
  [ReferralAuthorizationStatus.Active]: { badgeClass: 'status-active', label: 'Active' },
  [ReferralAuthorizationStatus.Expired]: { badgeClass: 'status-expired', label: 'Expired' },
  [ReferralAuthorizationStatus.Canceled]: { badgeClass: 'status-canceled', label: 'Cancelled' },
  [ReferralAuthorizationStatus.NotStarted]: { badgeClass: 'status-not-started', label: 'Not started' }
};

function formatDate(date: string): string {
  const parsed = DateTime.fromISO(date ?? '');
  return parsed.isValid ? parsed.toFormat(DATE_FORMAT) : MISSING_DATE;
}

function formatDateRange(startDate: string, expirationDate: string): string {
  return `${formatDate(startDate)} - ${formatDate(expirationDate)}`;
}

// Turn an unrecognized wire status into a readable label, e.g. `pending_review`
// -> `Pending review`, so a future 5th status still renders something sensible.
function humanizeStatus(status: string): string {
  const spaced = (status ?? '').replace(/_/g, ' ').trim();
  if (!spaced) {
    return 'Unknown';
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Read-only display surface: an unrecognized status (BE contract drift) must
// degrade to a neutral badge for that one row, never throw and blank the card.
// The strict contract is asserted in unit tests instead.
function styleFor(status: ReferralAuthorizationStatus): StatusStyle {
  return VA_AUTH_STATUS_STYLE[status]
    ?? {
      badgeClass: VA_AUTH_STATUS_STYLE[ReferralAuthorizationStatus.NotStarted].badgeClass,
      label: humanizeStatus(status)
    };
}

export function toVaAuthorizationRow(auth: ReferralAuthorization): VaAuthorizationRowVm {
  const style = styleFor(auth.status);

  return {
    // therapyType is the human-readable care-type label the table's "Care type"
    // column shows, so it maps straight to careType.
    authNumber: auth.authNumber,
    careType: auth.therapyType,
    dateRange: formatDateRange(auth.startDate, auth.expirationDate),
    statusLabel: style.label,
    badgeClass: style.badgeClass
  };
}
